import { v4 as uuidv4 } from 'uuid';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { URLParameters } from '@/app/common/urlParameters';
import { dropRestorableModal, renameRestorableModal, useNextRestorableModal } from '@/app/common/modalStack';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { createFlightClickedPayload } from '@/app/common/starTrack/events/flightClicked.ts';
import { getTrackingVcSearchId } from '@/app/common/tracking/trackingUtils.ts';
import { createFlightDetailsOpenedPayload } from '@/app/common/starTrack/events/flightDetailsOpened.ts';
import Co2Modal from '@/app/components/hotel-detail/flight-train/Co2Modal';
import { useBrowserBackButton } from '../hooks/useBrowserBackButton';
import { useCuriosity } from '../CuriosityProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { trackViewTransportDetailsClick } from '../trackInteractionEvents';
import useSelectedTransport from '../hooks/useSelectedTransport';
import { useInitialParams } from '../InitialParamsProvider';
import { trackVisitEvent } from '../trackVisitEvents';
import { useOffersContext } from '../OffersProvider';
import { useSelectedMealPlan } from '../SelectedMealPlanProvider';
import { useFetchHotelPromotions } from '../hooks/useFetchHotelPromotions';
import { useCreateAsyncPricing } from '../PricingProvider';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import getSearchParams from '../../search/common/getSearchParams';
import { getFlightKey } from '../common/flightKey';

export type SelectedTransportDetails = {
  id: string;
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  co2Footprint: CO2Footprint;
  priceIncrement?: number;
  eligibleBrandedFares: boolean;
  numberOfBaggagePiecesIncluded: number | null;
};

type ChangeTransportDetailsModalContextType = {
  closeModal: () => void;
  isOpen: boolean | undefined;
  selectedTransport: SelectedTransportDetails | undefined;
};

type ChangeTransportDetailsOpenModalContextType = {
  openModal: (transport: SelectedTransportDetails | undefined) => void;
  openCo2Modal: (co2Footprint: CO2Footprint) => void;
  setListedTransports: (transports: SelectedTransportDetails[] | undefined) => void;
};

const ChangeTransportDetailsModalContext = createContext<ChangeTransportDetailsModalContextType | undefined>(undefined);
const ChangeTransportDetailsOpenModalContext = createContext<ChangeTransportDetailsOpenModalContextType | undefined>(
  undefined
);

// MPH-969: mounted once per page — EvoDetailsProviders on Evo, whose change-flight drawer reads it
// from here, TransportLayout on the non-Evo flight page, and the package review. It used to be
// mounted a second time inside the drawer, with an isOpen of its own driving the same ?modal= stack.
const MODAL_ID_PREFIX = 'transport-details';

export function ChangeTransportDetailsModalProvider(props: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  const [selectedTransport, setSelectedTransport] = useState<SelectedTransportDetails | undefined>(undefined);
  const [isCo2ModalOpen, setIsCo2ModalOpen] = useState<boolean | undefined>(undefined);
  const [co2FootprintForModal, setCo2FootprintForModal] = useState<CO2Footprint | undefined>(undefined);
  // The flights the change-flight list is showing, while it is on screen. (MPH-969)
  const [listedTransports, setListedTransports] = useState<SelectedTransportDetails[] | undefined>(undefined);

  const { t } = useTranslation();
  const { data: selectedTransportData } = useSelectedTransport();
  const currentTransport = selectedTransportData?.transports[0];
  const nextRestorableModal = useNextRestorableModal();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { starTrack } = useCommonClient();
  const initialParams = useInitialParams();
  const { staticData, commonData, searchInfoDetailData, localization } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const { isTrain, accommodationOnly } = urlSearchParams;
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const vcSearchId = searchParams[URLParameters.VC_SEARCH_ID];
  const { handleFlightClicked } = useCommonClient();

  const { offersData } = useOffersContext();
  const { selectedMealPlan, selectedRoom } = useSelectedMealPlan();
  const { data: hotelPromotions } = useFetchHotelPromotions();
  const { data: asyncPricing } = useCreateAsyncPricing();
  const { data: hotelStaticData } = useFetchHotelStatic();

  const openModal = useCallback(
    (transport: SelectedTransportDetails | undefined) => {
      setIsOpen(true);
      if (transport) {
        setSelectedTransport(transport);
        const defaultFlight = selectedTransportData?.transports[0];
        // MPH-646: in Evo, viewing flight details is not a funnel step — FLIGHT_CLICKED is
        // emitted by the flight-page book click instead (useTrackEvoBookClicked), and the
        // flightClickId identifies the whole flight-viewing session (rotateEvoFlightClickId in
        // pageWrapperClientDetails) so all its funnel and branded-fares events join on it:
        // don't rotate it here.
        if (!localization.configValuesApp?.evolutionEnabled) {
          const flightClickId = uuidv4().slice(0, 32);
          handleFlightClicked(flightClickId, transport.eligibleBrandedFares);
          starTrack.flightClicked(
            createFlightClickedPayload({
              searchId: searchId || '',
              productDetailKey: initialParams.productDetailKey,
              wasDefaultFlight: transport?.id === defaultFlight?.id,
              transport,
              flightClickId: flightClickId || '',
              vcSearchId: getTrackingVcSearchId(offersData, vcSearchId),
            })
          );
        }
        trackVisitEvent({
          t,
          pageSection: 'hdp.transportDetails',
          mealPlan: selectedMealPlan ?? offersData?.cheapestFilteredMealPlan,
          roomName: selectedRoom?.name ?? offersData?.rooms?.[0]?.name ?? '',
          // The increment is measured from the selected package, so it only applies to that baseline
          priceIncrement: selectedMealPlan ? transport.priceIncrement : undefined,
          checkin: offersData?.checkin || '',
          checkout: offersData?.checkout || '',
          hotelStaticData,
          initialParams,
          promotionType: hotelPromotions?.promotionType || '',
          urlSearchParams,
          pricingId: asyncPricing?.pricingId,
          staticData,
          commonData,
          localization,
          selectedFlight: accommodationOnly ? undefined : transport,
          searchInfoDetailData,
        });
      }

      if (selectedTransport?.id === selectedTransportData?.transports[0]?.id) {
        starTrack.flightDetailsOpened(
          createFlightDetailsOpenedPayload({
            searchId: searchId || '',
            productDetailKey: initialParams.productDetailKey,
          })
        );
        sendCuriosityClickEvent({
          event_name: 'click_view_transport_details',
        });
        trackViewTransportDetailsClick(isTrain);
      } else {
        sendCuriosityClickEvent({
          event_name: 'click_view_transport_details',
          event_context: 'change_transport_page',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTransport?.id, selectedTransportData?.transports[0]?.id, selectedMealPlan, selectedRoom]
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    if (selectedTransport?.id !== selectedTransportData?.transports[0]?.id) {
      sendCuriosityClickEvent({
        event_name: 'click_close_transport_details',
        event_context: 'change_transport_page',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTransport?.id, selectedTransportData?.transports[0]?.id]);

  const openCo2Modal = useCallback((co2Footprint: CO2Footprint) => {
    setCo2FootprintForModal(co2Footprint);
    setIsCo2ModalOpen(true);
  }, []);

  const closeCo2Modal = useCallback(() => {
    setIsCo2ModalOpen(false);
  }, []);

  // MPH-969: the modal id carries the flight, and until a flight is picked this provider answers to
  // the bare prefix — so a details modal listed in the stack the page arrived on never matched it
  // and was never reopened, not even alone. When it is next in line, pick its flight first, from the
  // page's own flight or from the change-flight list once that is loaded, under the id it goes by now
  // (see getFlightKey): the id then matches and useBrowserBackButton reopens it. A flight the loaded
  // list no longer offers cannot be shown, so its id is dropped instead of waiting forever.
  useEffect(() => {
    if (!nextRestorableModal?.startsWith(`${MODAL_ID_PREFIX}-`)) return;
    const flightKey = getFlightKey(nextRestorableModal.slice(MODAL_ID_PREFIX.length + 1));
    const transport = [currentTransport, ...(listedTransports ?? [])].find(
      (candidate) => !!candidate && getFlightKey(candidate.id) === flightKey
    );
    if (transport) {
      renameRestorableModal(nextRestorableModal, `${MODAL_ID_PREFIX}-${transport.id}`);
      setSelectedTransport(transport);
    } else if (listedTransports) {
      dropRestorableModal(nextRestorableModal);
    }
  }, [nextRestorableModal, currentTransport, listedTransports]);

  useBrowserBackButton({
    condition: isOpen,
    modalId: selectedTransport?.id ? `${MODAL_ID_PREFIX}-${selectedTransport?.id}` : MODAL_ID_PREFIX,
    onClose: closeModal,
    onOpen: () => openModal(selectedTransport),
  });

  const changeTransportDetailsModalMemoizedValue = useMemo(
    () => ({
      closeModal,
      isOpen,
      selectedTransport,
    }),
    [closeModal, isOpen, selectedTransport]
  );

  const changeTransportDetailsOpenModalMemoizedValue = useMemo(
    () => ({
      openModal,
      openCo2Modal,
      setListedTransports,
    }),
    [openModal, openCo2Modal]
  );

  return (
    <ChangeTransportDetailsModalContext.Provider value={changeTransportDetailsModalMemoizedValue}>
      <ChangeTransportDetailsOpenModalContext.Provider value={changeTransportDetailsOpenModalMemoizedValue}>
        {props.children}
        {co2FootprintForModal && (
          <Co2Modal
            co2Footprint={co2FootprintForModal}
            isOpen={!!isCo2ModalOpen}
            onClose={closeCo2Modal}
            onOpenByBrowserHistory={() => setIsCo2ModalOpen(true)}
          />
        )}
      </ChangeTransportDetailsOpenModalContext.Provider>
    </ChangeTransportDetailsModalContext.Provider>
  );
}

export function useChangeTransportDetailsModal() {
  const context = useContext(ChangeTransportDetailsModalContext);

  if (context === undefined) {
    throw new Error('useChangeTransportDetailsModal must be within a ChangeTransportDetailsModalProvider');
  }

  return context;
}

export function useChangeTransportDetailsOpenModal() {
  const context = useContext(ChangeTransportDetailsOpenModalContext);

  if (context === undefined) {
    throw new Error('useChangeTransportDetailsOpenModal must be within a ChangeTransportDetailsModalProvider');
  }

  return context;
}

/**
 * Hands the provider the flights a change-flight list is showing, so that a details modal the page
 * arrived with can be reopened on one of them (undefined while the list is loading). (MPH-969)
 */
export function useListedTransportsForRestore(transports: SelectedTransportDetails[] | undefined) {
  const { setListedTransports } = useChangeTransportDetailsOpenModal();

  useEffect(() => {
    setListedTransports(transports);
    return () => setListedTransports(undefined);
  }, [transports, setListedTransports]);
}
